import React from "react";
import {
    CustomStyleFormHelperText,
} from "../Styles";
import {useTranslation} from "react-i18next";
import Cookies from "js-cookie";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Custom hook to handle text field behavior, styles, validation and character counting.
 *
 * @param {string} type Input type (e.g. "text", "number").
 * @param {number|string} length Maximum allowed length, use "MAX" for unlimited.
 * @param {string|number} height Custom height for the input element.
 * @param {string} backgroundColor Background color for the input.
 * @param {string} fontColor Font color for the input text.
 * @param {Object} inputProps Additional input properties passed to the component.
 * @param {boolean} countDecimals Enables decimal validation for numeric inputs.
 * @param {Function} onKeyDown Custom key down handler.
 * @param {Function} onChange Change handler for input value updates.
 * @param value
 */
const CustomTextFieldApp = ({
    type,
    length,
    height,
    backgroundColor,
    fontColor,
    inputProps,
    countDecimals,
    onKeyDown,
    onChange,
    value
}) => {
    const { t } = useTranslation();
    const lCode = Cookies.get('i18next') || 'en';
    const charsList = ['e', 'E', '-', '+'];
    const valueEmpty = value === null || value === '';

    let style = {
        backgroundColor: backgroundColor,
        borderRadius: "4px",
        width: "100%"
    };

    if (fontColor) {
        style = { ...style, color: fontColor, '-webkit-text-fill-color': fontColor };
    }

    if (height) {
        style = { ...style, height: height };
    }

    if (length !== "MAX") {
        inputProps = { ...inputProps, maxLength: length };
    }

    /**
     * Handles numeric input change validation.
     * Prevents invalid characters and enforces decimal and length restrictions.
     *
     * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e Input change event.
     */
    const handleNumberInputChange = (e) => {
        if (charsList.includes(e?.nativeEvent?.data) && type === "number") {
            e.preventDefault();
            return;
        }
        const newValue = e.target.value;
        const regex = countDecimals ? /^\d*\.?\d{0,1}$/ : /^\d*$/;
        if (regex.test(newValue) && newValue.length <= length) {
            onChange(e);
        }
    };

    /**
     * Handles key down events.
     * Prevents invalid characters for numeric inputs and delegates to custom handler if provided.
     *
     * @param {React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>} e Key down event.
     */
    const handleKeyDown = (e) => {
        if (charsList.includes(e.key) && type === "number") {
            e.preventDefault();
            return;
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    /**
     * Handles paste events.
     * Validates pasted content to prevent invalid characters in numeric inputs.
     *
     * @param {React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>} e Paste event.
     */
    const handlePaste = (e) => {
        const pasteData = e.clipboardData.getData('text');
        let isValid = true;

        for (let i = 0; i < pasteData.length; i++) {
            if (charsList.includes(pasteData[i]) && type === "number") {
                isValid = false;
                break;
            }
        }

        if (!isValid) {
            e.preventDefault();
            return;
        }
    }

    /**
     * Renders a character counter helper component.
     *
     * @param {Object} props CharacterCount props.
     * @param {number} props.count Current character count.
     * @param {string} props.helperText Helper text displayed on the left.
     * @param {number} props.max Maximum allowed characters.
     * @param {boolean} props.disabled Indicates whether the counter is disabled.
     * @param {string} props.counterColor Color for the counter text.
     * @param {boolean} props.showCounter Controls visibility of the counter.
     * @param {string} props.errorClass CSS class applied when error state is active.
     *
     * @returns {JSX.Element} Character counter component.
     */
    const CharacterCount = ({ count, helperText, max, disabled, counterColor, showCounter, errorClass , marginBottom}) => {
        return <Stack
            component="span"
            className={errorClass}
            direction="row"
            justifyContent={helperText ? 'space-between' : 'flex-end'}
            whiteSpace="nowrap"
            sx={CustomStyleFormHelperText(showCounter, marginBottom)}
            disabled={disabled}
        >
            {showCounter && <>
                <Box component="span" overflow="hidden" textOverflow="ellipsis">
                    {helperText}
                </Box>

                {!disabled ? (
                    <Box className={"counter-numbers"} component="span"
                         color={count >= max ? 'error.main' : counterColor}>
                        {count} / {max}
                    </Box>
                ) : (
                    <Box component="span" className={"mb-1rem"}/>
                )}
            </>}
        </Stack>

    };


    return {
        t,
        style,
        customInputProps: inputProps,
        lCode,
        valueEmpty,
        handleKeyDown,
        handleNumberInputChange,
        handlePaste,
        CharacterCount
    };
};

export default CustomTextFieldApp;
